import { sql } from "drizzle-orm";
import {
  bigint,
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";

import { balanceLedger, boxes, boxVersions, endUsers, environments, openGroups } from "./app.js";
import { tenant } from "./auth.js";
import { battlePlayers } from "./battles.js";

// ---- forge ------------------------------------------------------------------------------------
// A forge box is a COMMUNITY box a shopper built (`boxes.creator_end_user_id`). Its creator earns
// a cut of every purchase of it, accrued here as the purchase settles and paid into their balance
// when they claim (`claimForgeEarnings`, one `forge_commission` ledger row). See docs/forge.md.

/**
 * One purchase's commission for a forge box's creator, in thousandths of a cent, or the sub-cent
 * remainder a claim carried forward. Append-only apart from the claim stamp. Writer: the claim of
 * an open group (`claimReveal`, `sellBackReveal`), a battle entry (`createBattle`, `joinBattle`)
 * and `claimForgeEarnings` (`claimed_at`, `claim_ledger_id`, and the carry-forward row).
 *
 * Provenance is exactly one of `open_group_id` (a box purchase), `battle_player_id` +
 * `round_index` (one battle round a seat paid for) or `carried_from_ledger_id` (the remainder a
 * claim left). Each is unique, so accrual is idempotent. Accrual only ever inserts: it never locks
 * the creator's `end_users` row, so two creators opening each other's boxes cannot deadlock.
 * `amount_millicents` must never reach a tenant-facing response unfloored: money is shown in cents.
 */
export const forgeCommissionAccruals = pgTable(
  "forge_commission_accruals",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /**
     * The forge box the purchase was of. A carried remainder names the newest box of the claim that
     * left it, so a box's lifetime earnings sum its rows with `carried_from_ledger_id` null.
     */
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    /** The version the purchase pinned, whose `creator_cut_bps` priced the commission. */
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id),
    creatorEndUserId: text("creator_end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    openGroupId: text("open_group_id").references(() => openGroups.id, { onDelete: "cascade" }),
    battlePlayerId: text("battle_player_id").references(() => battlePlayers.id, {
      onDelete: "cascade",
    }),
    /** Set with `battle_player_id`: the round of the battle this row is the commission on. */
    roundIndex: integer("round_index"),
    /** The claim whose sub-cent remainder this row carries forward. */
    carriedFromLedgerId: text("carried_from_ledger_id").references(() => balanceLedger.id, {
      onDelete: "cascade",
    }),
    /** floor(purchase cents × cut bps / 10): thousandths of a cent, positive. */
    amountMillicents: bigint("amount_millicents", { mode: "number" }).notNull(),
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    /** The `forge_commission` ledger row that paid this accrual out. */
    claimLedgerId: text("claim_ledger_id").references(() => balanceLedger.id, {
      onDelete: "cascade",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("forge_accruals_open_group_idx")
      .on(t.openGroupId)
      .where(sql`${t.openGroupId} IS NOT NULL`),
    uniqueIndex("forge_accruals_battle_round_idx")
      .on(t.battlePlayerId, t.roundIndex)
      .where(sql`${t.battlePlayerId} IS NOT NULL`),
    uniqueIndex("forge_accruals_carried_idx")
      .on(t.carriedFromLedgerId)
      .where(sql`${t.carriedFromLedgerId} IS NOT NULL`),
    // A creator's unclaimed earnings: the claim and the "my forge" read.
    index("forge_accruals_creator_unclaimed_idx")
      .on(t.environmentId, t.creatorEndUserId)
      .where(sql`${t.claimedAt} IS NULL`),
    // A box's lifetime earnings: SUM over its source rows.
    index("forge_accruals_box_idx").on(t.boxId),
    check(
      "forge_accruals_one_source",
      sql`num_nonnulls(${t.openGroupId}, ${t.battlePlayerId}, ${t.carriedFromLedgerId}) = 1`,
    ),
    check(
      "forge_accruals_round_with_seat",
      sql`(${t.battlePlayerId} IS NULL) = (${t.roundIndex} IS NULL)`,
    ),
    check("forge_accruals_amount_positive", sql`${t.amountMillicents} > 0`),
    check(
      "forge_accruals_claim_stamped",
      sql`(${t.claimedAt} IS NULL) = (${t.claimLedgerId} IS NULL)`,
    ),
  ],
);
